import { type ReactNode } from "react";
import { IconBooks } from "@tabler/icons-react";
import { cn } from "../../lib/utils";
import { Avatar } from "./Avatar";

type JournalCardProps = {
  /** Journal name displayed as the primary title */
  title: string;
  /** Optional description rendered below the title, indented under the text column */
  description?: string;
  /** Slot rendered on the right side (typically a Follow/Join Button) */
  action?: ReactNode;
  /** Override the avatar icon (defaults to a "books" Tabler icon) */
  icon?: ReactNode;
  /** Override the avatar entirely (e.g. with an image) */
  avatar?: ReactNode;
  /** When true, hides the bottom border (last item in a list) */
  isLast?: boolean;
  /** Click handler for the entire card */
  onClick?: () => void;
  /** Additional class names merged onto the root element */
  className?: string;
};

function JournalCard({
  title,
  description,
  action,
  icon,
  avatar,
  isLast = false,
  onClick,
  className,
}: JournalCardProps): React.JSX.Element {
  const Wrapper = onClick == null ? "div" : "button";

  return (
    <Wrapper
      type={onClick == null ? undefined : "button"}
      onClick={onClick}
      className={cn(
        "flex w-full flex-col py-3 text-left",
        !isLast && "border-b border-ds-slate-200",
        onClick != null && "cursor-pointer",
        className
      )}
    >
      <div className="flex w-full items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1 overflow-clip">
          <div className="flex w-full items-center gap-2">
            {avatar != null ? (
              avatar
            ) : (
              <Avatar
                variant="icon"
                size="md"
                icon={icon ?? <IconBooks stroke={1.5} />}
                badge={null}
              />
            )}
            <span className="truncate text-[15px]/5 font-semibold tracking-[-0.15px] text-ds-black">
              {title}
            </span>
          </div>
          {description != null && (
            <p className="line-clamp-4 pl-[46px] text-[11px]/4 tracking-[-0.11px] text-ds-slate-500">
              {description}
            </p>
          )}
        </div>
        {action != null && <div className="shrink-0">{action}</div>}
      </div>
    </Wrapper>
  );
}

export { JournalCard, type JournalCardProps };
